'use client';

import { c as _c } from "react/compiler-runtime";
import { convictionFraction, useGetMarketField, useMarketConfig } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { DocsLinks } from '@/components/how-it-works/docs-links';
import { InteractiveExplainerButton } from '@/components/how-it-works/interactive-explainer-button';
import { buildOutcomeRowSummaries } from '@/components/markets/outcome-row-summary';
import { Award01Icon, Coins01Icon, JusticeScale01Icon, Search02Icon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { formatPercent } from '@/lib/chart/format';
import { useFormattedCurrency } from '@/lib/currency';
import { useSimulatedTradeModalStore } from '@/stores/simulated-trade-modal';
interface MarketHowItWorksModalProps {
  marketPubkey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the "What is graduation?" sub-modal from an inline cross-link. */
  onWhatIsGraduation?: () => void;
  /** Opens the "Preventing manipulation" sub-modal from an inline cross-link. */
  onPreventManipulation?: () => void;
}

/**
 * "How this market works" — the per-market explainer opened from the `?` button
 * in the market detail header. Unlike the generic `MarketsHowItWorksModal`, this
 * one threads live data for THIS market (outcome count, current leader + its
 * share, the resolution threshold, and the prize pool) so the copy reads as a
 * narration of the market in front of the user rather than an abstract overview.
 *
 * All dynamic values are read through `useGetMarketField` field leaves so they
 * share the detail page's cache entry (no extra fetch) and stay live over NATS.
 */
export function MarketHowItWorksModal(t0) {
  const $ = _c(96);
  const {
    marketPubkey,
    open,
    onOpenChange,
    onWhatIsGraduation,
    onPreventManipulation
  } = t0;
  const t = useTranslations("market_how_it_works");
  const {
    formatD6
  } = useFormattedCurrency();
  const openSimulatedTrade = useSimulatedTradeModalStore(_temp);
  let t1;
  if ($[0] !== marketPubkey) {
    t1 = {
      pubkey: marketPubkey
    };
    $[0] = marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const detailArgs = t1;
  const outcomes = useGetMarketField(detailArgs, "outcomes");
  const config = useMarketConfig(useGetMarketField(detailArgs, "marketConfig"));
  const thresholdD9 = config?.convictionResolutionThresholdPpb;
  const prizePoolD6 = useGetMarketField(detailArgs, "prizePool");
  let T0;
  let T1;
  let T2;
  let earnDescription;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== formatD6 || $[3] !== onOpenChange || $[4] !== open || $[5] !== openSimulatedTrade || $[6] !== outcomes || $[7] !== prizePoolD6 || $[8] !== t || $[9] !== thresholdD9) {
    const thresholdFraction = convictionFraction(thresholdD9);
    const thresholdLabel = formatPercent(thresholdFraction, 0);
    let hasPrizePool;
    let prizePoolValue;
    let t13;
    if ($[25] !== outcomes || $[26] !== prizePoolD6 || $[27] !== t) {
      const summaries = outcomes !== undefined ? buildOutcomeRowSummaries(outcomes) : [];
      const outcomeCount = summaries.length;
      const leader = summaries.toSorted(_temp2)[0];
      const hasLeader = leader !== undefined && leader.share > 0;
      prizePoolValue = String(prizePoolD6 ?? "0");
      hasPrizePool = Number(prizePoolValue) > 0;
      t13 = hasLeader ? t("question_description", {
        count: outcomeCount,
        leader: leader.label,
        leaderPct: formatPercent(leader.share, 1)
      }) : t("question_description_no_leader", {
        count: outcomeCount
      });
      $[25] = outcomes;
      $[26] = prizePoolD6;
      $[27] = t;
      $[28] = hasPrizePool;
      $[29] = prizePoolValue;
      $[30] = t13;
    } else {
      hasPrizePool = $[28];
      prizePoolValue = $[29];
      t13 = $[30];
    }
    const questionDescription = t13;
    let t14;
    if ($[31] !== formatD6 || $[32] !== hasPrizePool || $[33] !== prizePoolValue || $[34] !== t) {
      t14 = hasPrizePool ? t("earn_description", {
        prizePool: formatD6(prizePoolValue, "compact")
      }) : t("earn_description_no_pool");
      $[31] = formatD6;
      $[32] = hasPrizePool;
      $[33] = prizePoolValue;
      $[34] = t;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    earnDescription = t14;
    T2 = InfoModal;
    t7 = open;
    t8 = onOpenChange;
    t9 = "lg";
    if ($[36] !== t) {
      t10 = t("title");
      $[36] = t;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    t11 = t("description", {
      threshold: thresholdLabel
    });
    let t15;
    if ($[38] !== t) {
      t15 = t("got_it");
      $[38] = t;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    let t16;
    if ($[40] !== onOpenChange) {
      t16 = () => onOpenChange(false);
      $[40] = onOpenChange;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    let t17;
    if ($[42] !== onOpenChange || $[43] !== openSimulatedTrade) {
      t17 = <InteractiveExplainerButton onOpen={() => {
        onOpenChange(false);
        openSimulatedTrade(true);
      }} />;
      $[42] = onOpenChange;
      $[43] = openSimulatedTrade;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    if ($[45] !== t15 || $[46] !== t16 || $[47] !== t17) {
      t12 = <InfoModal.Footer cta={t15} onClose={t16} lead={t17} />;
      $[45] = t15;
      $[46] = t16;
      $[47] = t17;
      $[48] = t12;
    } else {
      t12 = $[48];
    }
    T1 = InfoModal.IconList;
    let t18;
    if ($[49] !== t) {
      t18 = t("question_title");
      $[49] = t;
      $[50] = t18;
    } else {
      t18 = $[50];
    }
    if ($[51] !== questionDescription || $[52] !== t18) {
      t5 = <InfoModal.IconItem icon={Search02Icon} title={t18} description={questionDescription} />;
      $[51] = questionDescription;
      $[52] = t18;
      $[53] = t5;
    } else {
      t5 = $[53];
    }
    let t19;
    if ($[54] !== t) {
      t19 = t("back_title");
      $[54] = t;
      $[55] = t19;
    } else {
      t19 = $[55];
    }
    let t20;
    if ($[56] !== t) {
      t20 = t("back_description");
      $[56] = t;
      $[57] = t20;
    } else {
      t20 = $[57];
    }
    if ($[58] !== t19 || $[59] !== t20) {
      t6 = <InfoModal.IconItem icon={Coins01Icon} title={t19} description={t20} />;
      $[58] = t19;
      $[59] = t20;
      $[60] = t6;
    } else {
      t6 = $[60];
    }
    T0 = InfoModal.IconItem;
    t2 = JusticeScale01Icon;
    t3 = t("resolve_title", {
      threshold: thresholdLabel
    });
    t4 = t("resolve_description", {
      threshold: thresholdLabel
    });
    $[2] = formatD6;
    $[3] = onOpenChange;
    $[4] = open;
    $[5] = openSimulatedTrade;
    $[6] = outcomes;
    $[7] = prizePoolD6;
    $[8] = t;
    $[9] = thresholdD9;
    $[10] = T0;
    $[11] = T1;
    $[12] = T2;
    $[13] = earnDescription;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[10];
    T1 = $[11];
    T2 = $[12];
    earnDescription = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t2 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t13;
  if ($[61] !== T0 || $[62] !== t2 || $[63] !== t3 || $[64] !== t4) {
    t13 = <T0 icon={t2} title={t3} description={t4} />;
    $[61] = T0;
    $[62] = t2;
    $[63] = t3;
    $[64] = t4;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  let t14;
  if ($[66] !== t) {
    t14 = t("earn_title");
    $[66] = t;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  let t15;
  if ($[68] !== earnDescription || $[69] !== t14) {
    t15 = <InfoModal.IconItem icon={Award01Icon} title={t14} description={earnDescription} />;
    $[68] = earnDescription;
    $[69] = t14;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  let t16;
  if ($[71] !== T1 || $[72] !== t13 || $[73] !== t15 || $[74] !== t5 || $[75] !== t6) {
    t16 = <T1>{t5}{t6}{t13}{t15}</T1>;
    $[71] = T1;
    $[72] = t13;
    $[73] = t15;
    $[74] = t5;
    $[75] = t6;
    $[76] = t16;
  } else {
    t16 = $[76];
  }
  let t17;
  if ($[77] !== onWhatIsGraduation || $[78] !== t) {
    t17 = onWhatIsGraduation !== undefined ? [{
      key: "graduation",
      label: t("graduation_link"),
      onClick: onWhatIsGraduation
    }] : [];
    $[77] = onWhatIsGraduation;
    $[78] = t;
    $[79] = t17;
  } else {
    t17 = $[79];
  }
  let t18;
  if ($[80] !== onPreventManipulation || $[81] !== t) {
    t18 = onPreventManipulation !== undefined ? [{
      key: "manipulation",
      label: t("manipulation_link"),
      onClick: onPreventManipulation
    }] : [];
    $[80] = onPreventManipulation;
    $[81] = t;
    $[82] = t18;
  } else {
    t18 = $[82];
  }
  let t19;
  if ($[83] !== t17 || $[84] !== t18) {
    t19 = <DocsLinks docsPath="/concepts/opinion-markets" crossLinks={[...t17, ...t18]} />;
    $[83] = t17;
    $[84] = t18;
    $[85] = t19;
  } else {
    t19 = $[85];
  }
  let t20;
  if ($[86] !== T2 || $[87] !== t10 || $[88] !== t11 || $[89] !== t12 || $[90] !== t16 || $[91] !== t19 || $[92] !== t7 || $[93] !== t8 || $[94] !== t9) {
    t20 = <T2 open={t7} onOpenChange={t8} size={t9} title={t10} description={t11} footer={t12}>{t16}{t19}</T2>;
    $[86] = T2;
    $[87] = t10;
    $[88] = t11;
    $[89] = t12;
    $[90] = t16;
    $[91] = t19;
    $[92] = t7;
    $[93] = t8;
    $[94] = t9;
    $[95] = t20;
  } else {
    t20 = $[95];
  }
  return t20;
}
function _temp2(a, b) {
  return b.share - a.share;
}
function _temp(s) {
  return s.setOpen;
}